import { indexEnumJson, type EnumJson } from './enum-maps'
import type { GrammarSlot } from './grammar'
import type { TagGrammarPair, TagLinkExclude, TagMappingSpec } from './match'

/**
 * Loose HTTP / wizard JSON body for tag-mapping suggest, preview, and apply.
 * Accepts camelCase and snake_case aliases used by the BFF and the UI.
 */
export interface TagMappingWireBody {
  mappingTypeId?: unknown
  mapping_type_id?: unknown
  siteId?: unknown
  site_id?: unknown
  aGrammar?: unknown
  a_grammar?: unknown
  bGrammar?: unknown
  b_grammar?: unknown
  enumMaps?: unknown
  enum_maps?: unknown
  grammars?: unknown
  enums?: unknown
  aSamples?: unknown
  a_samples?: unknown
  bSamples?: unknown
  b_samples?: unknown
  manualLinks?: unknown
  manual_links?: unknown
}

export interface ParsedTagMappingWire {
  mappingTypeId: string
  siteId: string
  spec: TagMappingSpec
  aSamples: string[]
  bSamples: string[]
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

export function parseTagMappingLinkExcludes(value: unknown): TagLinkExclude[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const rows: TagLinkExclude[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
    const row = entry as Record<string, unknown>
    const aName = text(row.aName ?? row.a_name)
    const bName = text(row.bName ?? row.b_name)
    if (!aName || !bName) continue
    const key = `${aName.toUpperCase()}\0${bName.toUpperCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    rows.push({ aName, bName })
  }
  return rows
}

export function parseTagMappingStringList(value: unknown): string[] {
  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
  }
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => entry.length > 0)
}

function grammarSlots(value: unknown): GrammarSlot[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const name = text(row.name)
    if (!name) return []
    return [
      {
        name,
        type: text(row.type) || 'string',
        regex: typeof row.regex === 'string' ? row.regex : ''
      }
    ]
  })
}

function enumObject(value: unknown): EnumJson | undefined {
  try {
    const indexed = indexEnumJson(value)
    if (Object.keys(indexed.values).length === 0) return undefined
    if (typeof value === 'string') return indexed.values
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
    return value as EnumJson
  } catch {
    return undefined
  }
}

export function parseTagGrammarPairs(value: unknown): TagGrammarPair[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const a = grammarSlots(row.a)
    const b = grammarSlots(row.b)
    const aGrammar = text(row.aGrammar ?? row.a_grammar)
    const bGrammar = text(row.bGrammar ?? row.b_grammar)
    if ((a.length === 0 || b.length === 0) && (!aGrammar || !bGrammar)) {
      return []
    }
    const pair: TagGrammarPair = {
      id: text(row.id) || `grammar-${index + 1}`
    }
    if (a.length > 0) pair.a = a
    if (b.length > 0) pair.b = b
    if (aGrammar) pair.aGrammar = aGrammar
    if (bGrammar) pair.bGrammar = bGrammar
    const label = text(row.label)
    if (label) pair.label = label
    const rationale = text(row.rationale)
    if (rationale) pair.rationale = rationale
    const enums = enumObject(row.enums)
    if (enums) pair.enums = enums
    const join = parseTagMappingStringList(row.join)
    if (join.length > 0) pair.join = join
    const enumMaps = typeof row.enumMaps === 'string' ? row.enumMaps : text(row.enum_maps)
    if (enumMaps) pair.enumMaps = enumMaps
    const exclude = parseTagMappingLinkExcludes(row.exclude)
    if (exclude.length > 0) pair.exclude = exclude
    return [pair]
  })
}

export function parseTagMappingSpec(body: TagMappingWireBody): TagMappingSpec {
  const grammars = parseTagGrammarPairs(body.grammars)
  const enums = enumObject(body.enums)
  const manualLinks = parseTagMappingLinkExcludes(body.manualLinks ?? body.manual_links)
  const manuals = manualLinks.length > 0 ? { manualLinks } : {}
  if (grammars.length > 0) {
    return { grammars, ...(enums ? { enums } : {}), ...manuals }
  }
  return {
    aGrammar: text(body.aGrammar ?? body.a_grammar),
    bGrammar: text(body.bGrammar ?? body.b_grammar),
    enumMaps: typeof body.enumMaps === 'string' ? body.enumMaps : text(body.enum_maps),
    ...(enums ? { enums } : {}),
    ...manuals
  }
}

/** Normalize a suggest / preview / apply request body into a compiled-ready spec. */
export function parseTagMappingWireBody(body: TagMappingWireBody): ParsedTagMappingWire {
  return {
    mappingTypeId: text(body.mappingTypeId ?? body.mapping_type_id),
    siteId: text(body.siteId ?? body.site_id),
    spec: parseTagMappingSpec(body),
    aSamples: parseTagMappingStringList(body.aSamples ?? body.a_samples),
    bSamples: parseTagMappingStringList(body.bSamples ?? body.b_samples)
  }
}
